import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useSidebar } from "../../content/SidebarContent";
import PageHeader from "../../components/PageHeader";
import {
  reqToGetTenantDetails,
  reqToEditTenant,
} from "../../redux/slices.js/tenants/tenantsSlice";
import { routes } from "../../helpers/constants";
import { reqToGetPlanList } from "../../redux/slices.js/plans/plansSlice";
import {
  formatDateToYMD,
  displayDateAsDMY,
} from "../../helpers/commonFunctions";
import { reqToGetCouponList } from "../../redux/slices.js/promotioncoupons/couponSlice";
import logo from "../../assets/images/logo.svg";
import { IMAGE_URL } from "../../config";

const companyTypeOptions = [
  { value: "Private", label: "Private" },
  { value: "Public", label: "Public" },
  { value: "Startup", label: "Startup" },
];

const teamStrengthOptions = [
  { value: "1-10", label: "1-10" },
  { value: "11-50", label: "11-50" },
  { value: "51-200", label: "51-200" },
  { value: "200+", label: "200+" },
];

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is Required!"),
  name: Yup.string().required("Name is Required!"),
  companyName: Yup.string().required("Company Name is Required!"),
  companyType: Yup.object().required("Company Type is Required!"),
  teamStrength: Yup.object().required("Team Strength is Required!"),
  activePlan: Yup.object().required("Active Plan is Required!"),
  // website: Yup.string()
  //   .url("Invalid website URL")
  //   .required("Business Website is Required!"),
  // socialMedia: Yup.string()
  //   .url("Invalid social media URL")
  //   .required("Social Media URL is Required!"),
});

const EditTenant = () => {
  const { setIsSidebarOpen } = useSidebar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const tabs = ["Tenant Details", "Subscription", "Marketing Persona"];
  const [activeTab, setActiveTab] = useState("Tenant Details");

  const tenant = useSelector((state) => state.tenants.tenantDeatils);

  const plans = useSelector((state) => state.plans.plans);
  const planOptions = plans.map((plan) => ({
    label: plan.name,
    value: plan.id,
  }));
  const coupons = useSelector((state) => state.promotionCoupons.CouponList);
  const couponOptions = coupons.map((coupon) => ({
    label: coupon.name,
    value: coupon.id,
  }));

  const staff = useSelector((state) => state.staff.staffList);
  const staffOptions = staff.map((staff) => ({
    label: staff.name,
    value: staff.id,
  }));

  useEffect(() => {
    dispatch(
      reqToGetTenantDetails({
        id: Number(id),
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
    dispatch(
      reqToGetPlanList({
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
    dispatch(
      reqToGetCouponList({
        data: {
          tab: "private",
          search: "",
          page: 1,
          limit: 100,
        },
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, []);

  const initialValues = {
    email: tenant.email || "",
    name: tenant.name || "",
    companyName: tenant.tenant?.name || "",
    companyType:
      companyTypeOptions.find(
        (opt) => opt.value === tenant.tenant?.tenant_type
      ) || null,
    teamStrength:
      teamStrengthOptions.find(
        (opt) => opt.value === tenant?.tenant?.team_strength
      ) || null,
    activePlan:
      planOptions.find(
        (opt) => opt.value === tenant?.tenant?.subscription?.plan_id
      ) || null,
    extended_trial_date: tenant?.tenant?.subscription?.end_date
      ? new Date(tenant.tenant.subscription.end_date)
      : null,
    coupon:
      couponOptions.find(
        (opt) => opt.value === tenant?.tenant?.subscription?.coupon_id
      ) || null,
    staff:
      staffOptions.find(
        (opt) => opt.value === tenant?.tenant?.assigned_staff_id
      ) || null,
    website: tenant?.tenant?.website_url || "",
    socialMedia: tenant?.tenant?.social_media_url || "",
  };

  const activePlanName =
    planOptions.find(
      (opt) => opt.value === tenant?.tenant?.subscription?.plan_id
    )?.label || null;

  const handleSubmit = (values) => {
    let payload = {};

    if (activeTab === "Tenant Details") {
      payload = {
        email: values.email,
        admin_name: values.name,
        company_name: values.companyName,
        company_type: values.companyType.value,
        team_strength: values.teamStrength.value,
        website_url: values.website,
        social_media_url: values.socialMedia,
        assigned_staff_id: values.staff ? values.staff.value : null,
        coupon_id: values.coupon ? values.coupon.value : null,
      };
    }

    if (activeTab === "Subscription") {
      payload = {
        plan_id: values.activePlan.value,
        extended_trial_date: formatDateToYMD(values.extended_trial_date),
        coupon_id: values.coupon ? values.coupon.value : null,
      };
    }

    // ✅ Always dispatch here, after payload is set
    dispatch(
      reqToEditTenant({
        id: Number(id),
        data: payload,
        onSuccess: () => navigate(routes.tenants),
        onFailure: () => {},
      })
    );
  };

  return (
    <section className="dashboard-page main-layout-page main-layout h-[calc(100vh-50px)]">
      <div className="!p-0">
        <div className="main-layout-wrapper bg-white h-screen flex flex-col sticky z-1 top-0">
          <PageHeader
            title="Edit Tenant"
            description="Update tenant profile for your organization."
            onOpenSidebar={() => setIsSidebarOpen(true)}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          <div className="flex items-baseline gap-[40px] main-sidebar-wrapper flex-1">
            <div className="flex-1 right-sidebar h-full relative">
              <div className="right-sidebar-contend py-6 px-3 md:px-6 h-full">
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  enableReinitialize
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue, touched, errors }) => (
                    <Form className="signin-form flex flex-col gap-6 h-full">
                      <div className="bg-white box-shadow p-5 space-y-7 rounded-md">
                        {activeTab === "Tenant Details" && (
                          <>
                            <h2 className="text-h4 text-blue">
                              Tenant Information
                            </h2>
                            <div className="space-y-4">
                              <div class="list gap-3 grid grid-cols-1 md:grid-cols-2">
                                <div class="flex items-center gap-2 text-sub_span text-[#454545]">
                                  <span class="!font-normal">
                                    Total Employees:
                                  </span>{" "}
                                  {tenant?.total_employee}
                                </div>
                                <div className="flex justify-around items-center gap-2 text-sub_span text-[#454545]">
                                  <span className="!font-normal">
                                    Active Employees:
                                    {tenant?.active_employee}
                                  </span>
                                  <img
                                    src={
                                      IMAGE_URL + tenant?.tenant?.logo || logo
                                    }
                                    alt="Company Logo"
                                    className="w-20 h-20 rounded-full object-cover"
                                  />
                                </div>

                                <div class="flex items-center gap-2 text-sub_span text-[#454545]">
                                  <span class="!font-normal">
                                    Inactive Employees:
                                  </span>{" "}
                                  {tenant?.inactive_employee}
                                </div>
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label htmlFor="email">
                                  Email<span>*</span>
                                </label>
                                <Field
                                  name="email"
                                  type="email"
                                  placeholder="Enter email"
                                  className="w-full"
                                  disabled
                                />
                                <ErrorMessage
                                  name="email"
                                  component="div"
                                  className="text-red-500 text-sm mt-1"
                                />
                              </div>

                              <div>
                                <label htmlFor="name">
                                  Admin Name<span>*</span>
                                </label>
                                <Field
                                  name="name"
                                  placeholder="Enter admin name"
                                  className="w-full"
                                />
                                <ErrorMessage
                                  name="name"
                                  component="div"
                                  className="text-red-500 text-sm mt-1"
                                />
                              </div>

                              <div>
                                <label htmlFor="companyName">
                                  Company Name<span>*</span>
                                </label>
                                <Field
                                  name="companyName"
                                  placeholder="Enter company name"
                                  className="w-full"
                                />
                                <ErrorMessage
                                  name="companyName"
                                  component="div"
                                  className="text-red-500 text-sm mt-1"
                                />
                              </div>

                              <div>
                                <label htmlFor="companyType">
                                  Company Type<span>*</span>
                                </label>
                                <Select
                                  name="companyType"
                                  options={companyTypeOptions}
                                  value={values.companyType}
                                  onChange={(option) =>
                                    setFieldValue("companyType", option)
                                  }
                                  className="react-select-container w-full"
                                  classNamePrefix="react-select"
                                  isClearable={false}
                                  isSearchable={false}
                                />
                                {errors.companyType && touched.companyType && (
                                  <div className="text-red-500 text-sm mt-1">
                                    {errors.companyType}
                                  </div>
                                )}
                              </div>

                              <div>
                                <label htmlFor="teamStrength">
                                  Team Strength<span>*</span>
                                </label>
                                <Select
                                  name="teamStrength"
                                  options={teamStrengthOptions}
                                  value={values.teamStrength}
                                  onChange={(option) =>
                                    setFieldValue("teamStrength", option)
                                  }
                                  className="react-select-container w-full"
                                  classNamePrefix="react-select"
                                  isClearable={false}
                                  isSearchable={false}
                                />
                                {errors.teamStrength &&
                                  touched.teamStrength && (
                                    <div className="text-red-500 text-sm mt-1">
                                      {errors.teamStrength}
                                    </div>
                                  )}
                              </div>

                              <div>
                                <label htmlFor="website">
                                  Business Website<span>*</span>
                                </label>
                                <Field
                                  name="website"
                                  placeholder="Enter business website"
                                  className="w-full"
                                />
                                <ErrorMessage
                                  name="website"
                                  component="div"
                                  className="text-red-500 text-sm mt-1"
                                />
                              </div>

                              <div>
                                <label htmlFor="socialMedia">
                                  Social Media URL<span>*</span>
                                </label>
                                <Field
                                  name="socialMedia"
                                  placeholder="Enter social media profile link"
                                  className="w-full"
                                />
                                <ErrorMessage
                                  name="socialMedia"
                                  component="div"
                                  className="text-red-500 text-sm mt-1"
                                />
                              </div>

                              {/* <div>
                                <label htmlFor="coupon">Assign Coupon</label>
                                <Select
                                  name="coupon"
                                  options={couponOptions}
                                  value={values.coupon}
                                  onChange={(option) =>
                                    setFieldValue("coupon", option)
                                  }
                                  className="react-select-container w-full"
                                  classNamePrefix="react-select"
                                  placeholder="Select Coupon"
                                  isClearable={true}
                                  isSearchable={true}
                                />
                              </div> */}

                              <div>
                                <label htmlFor="staff">Assign Staff</label>
                                <Select
                                  name="staff"
                                  options={staffOptions}
                                  value={values.staff}
                                  placeholder="Select Staff"
                                  onChange={(option) =>
                                    setFieldValue("staff", option)
                                  }
                                  className="react-select-container w-full"
                                  classNamePrefix="react-select"
                                  isClearable={true}
                                  isSearchable={true}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {activeTab === "Subscription" && (
                          <>
                            <h2 className="text-h4 text-blue">Subscription</h2>

                            <div className="space-y-4">
                              <div class="list gap-3 grid grid-cols-1 md:grid-cols-2">
                                <div class="flex items-center gap-2 text-sub_span text-[#454545]">
                                  <span class="!font-normal">
                                    Current Subscription Plan:
                                  </span>{" "}
                                  {activePlanName}
                                </div>
                                <div class="flex items-center gap-2 text-sub_span text-[#454545]">
                                  <span class="!font-normal">Start Date:</span>{" "}
                                  {displayDateAsDMY(
                                    tenant?.tenant?.subscription?.start_date
                                  )}
                                </div>
                                <div class="flex items-center gap-2 text-sub_span text-[#454545]">
                                  <span class="!font-normal">End Date:</span>{" "}
                                  {displayDateAsDMY(
                                    tenant?.tenant?.subscription?.end_date
                                  )}
                                </div>
                                <div class="flex items-center gap-2 text-sub_span text-[#454545]">
                                  <span class="!font-normal">
                                    Employee Limit:
                                  </span>{" "}
                                  {
                                    tenant?.tenant?.subscription
                                      ?.max_active_users
                                  }{" "}
                                  Employees
                                </div>
                                {/* <div class="flex items-center gap-2 text-sub_span text-[#454545]">
                                  <span class="!font-normal">Total Amount Paid:</span> ₹45,000

                                </div> */}
                              </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label htmlFor="activePlan">
                                  Active Plan<span>*</span>
                                </label>
                                <Select
                                  name="activePlan"
                                  options={planOptions}
                                  value={values.activePlan}
                                  onChange={(option) =>
                                    setFieldValue("activePlan", option)
                                  }
                                  className="react-select-container w-full"
                                  classNamePrefix="react-select"
                                  isClearable={false}
                                  isSearchable={false}
                                />
                                {errors.activePlan && touched.activePlan && (
                                  <div className="text-red-500 text-sm mt-1">
                                    {errors.activePlan}
                                  </div>
                                )}
                              </div>

                              <div>
                                <label htmlFor="extended_trial_date">
                                  Extend Subscription<span>*</span>
                                </label>
                                <DatePicker
                                  selected={values.extended_trial_date}
                                  onChange={(date) =>
                                    setFieldValue("extended_trial_date", date)
                                  }
                                  className="w-full p-2 border rounded"
                                  dateFormat="yyyy-MM-dd"
                                  minDate={new Date()}
                                />
                                {errors.extended_trial_date &&
                                  touched.extended_trial_date && (
                                    <div className="text-red-500 text-sm mt-1">
                                      {errors.extended_trial_date}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </>
                        )}

                        {activeTab === "Marketing Persona" && (
                          <>
                            <h2 className="text-h4 text-blue">
                              Marketing Persona
                            </h2>

                            <div className="space-y-4">
                              <h2>Marketing Persona</h2>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="flex gap-4 mt-auto">
                        <button type="submit" className="login-btn text-white">
                          Update Tenant
                        </button>
                        <button
                          type="button"
                          className="skip-btn border-blue"
                          onClick={() => navigate(-1)}
                        >
                          Back
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditTenant;
